// Angular Core
import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

// Services
import { ClientRequestService } from 'src/app/features/services';

// Validators
import { rutEmpresaValidator, rutValidator } from '../../../shared/validators/rut.validator';
import { ClientSearchService, GtmService, LoadingService } from 'src/app/shared/services';
import { AliasInputLogType, InputLog } from '../../models/log/InputLog';
import { Status } from '../../models/log/status';
import { Compliance } from './enums/compliance.enum';
import { LogComplianceService } from './services/log-compliance.service';
import { DatosCliente } from './interfaces/datos-cliente.interface';
import { UtilService } from './services/util.service';
import { mensajePorDefecto, mensajesCompliance } from './constants/mensaje-compliance.const';
import { nombreValidator } from 'src/app/shared/validators/nombre.validator';
import { DatosProductoBELModel } from '../../models';
import { FeatureVarsService } from '@shared/services/feature-vars/feature-vars.service';

@Component({
    selector: 'app-compliance',
    templateUrl: './compliance.page.html',
    styleUrls: ['./compliance.page.scss'],
})

export class CompliancePage {
    mensajeInicioDesktop: string = Compliance.MensajeInicioDesktop;
    mensajeInicioMobile: string = Compliance.MensajeInicioMobile;
    botonVisible: boolean = false;
    rutClienteLimpio: string = '';
    mensajeCompliance: string;
    activarBoton: boolean = false;
    disabledForm: boolean = false;
    showMessage: boolean = false;
    datosCliente: DatosCliente;
    nombreCliente: string;
    apellidoCliente: string;
    rutCliente: string;
    tipoMensaje: string;
    tituloMensaje: string;
    mandato: boolean;

    logtemp: Partial<InputLog> & Pick<InputLog, AliasInputLogType> = {
        Categoria: "Buscador Cliente Cumplimiento",
        Operacion: "Buscador Cliente Cumplimiento",
        Linea_negocio: "VIDA",
        Metodo: "searchClient()",
        eventoLog: "Click boton buscar",
        status: Status.OK,
        Secuencia: 0,
        EntradaLog: {
            accion: 'Click boton buscar buscando',
        },
    };

    cumplimientoForm = new FormGroup({
        nombreCliente: new FormControl({ value: '', disabled: false }, [Validators.required, nombreValidator]),
        apellidoCliente: new FormControl({ value: '', disabled: false }, [Validators.required, nombreValidator]),
        rutCliente: new FormControl({ value: '', disabled: false }, [rutValidator, rutEmpresaValidator]),
    });

    constructor(
        private clientRequestService: ClientRequestService,
        private loaderService: LoadingService,
        private gtmService: GtmService,
        private logCompliance: LogComplianceService,
        private utilService: UtilService,
        private featureVarsService: FeatureVarsService,
        private clientSearchService: ClientSearchService
    ) { }

    async searchClient() {
        if (this.activarBoton) {
            return;
        }
        if (!this.cumplimientoForm.valid) {
            this.utilService.validateAllFormFields(this.cumplimientoForm);
            return;
        }

        this.datosCliente = this.cumplimientoForm.value;
        this.nombreCliente = this.datosCliente.nombreCliente;
        this.apellidoCliente = this.datosCliente.apellidoCliente;
        this.rutCliente = this.datosCliente.rutCliente;

        const mandato = await this.clientRequestService.getSignedMandate({ rut: this.datosCliente.rutCliente });
        this.mandato = mandato?.mandatoSuscrito;

        let [rut, dv] = this.utilService.limpiarRutCliente(this.datosCliente.rutCliente);
        this.rutClienteLimpio = `${rut}-${dv}`;
        this.loaderService.showLoading(false);

        this.clientRequestService.searchClientCompliance({ rut: this.rutClienteLimpio })
            .then((clientSearchModel: any) => {
                this.filtrarDatosCliente(clientSearchModel);
            }).catch(async (error) => {
                console.log(error)
                this.gtmService.pushTagEventoInteractivo('evento-interactivo', 'Buscar estado Cumplimiento', 'Click', 'Sin Resultados');
                this.logtemp.status = Status.NOK
                this.logCompliance.logData(
                    this.cumplimientoForm.value,
                    this.logtemp,
                    "log_error",
                );
                const codigoMarcaCompliance = error.status === 403 ? 6 : 5;
                await this.obtenerMensajeCompliance(this.rutClienteLimpio, codigoMarcaCompliance);
                this.botonVisible = true;
            });
    }

    filtrarDatosCliente(datosGuc) {
        const productosGuc = datosGuc.DatosProductoGeneral.DatosProductoBEL;
        if (this.clientSearchService.validarSegmento(datosGuc) && datosGuc.EstadoCliente === "V") {
            const listAFP: DatosProductoBELModel[] = productosGuc.filter((item) => {
                return item.DescripcionLinea === 'AFP' || item.DescripcionLinea === 'COL';
            });
            if (listAFP.length === datosGuc.DatosProductoGeneral.DatosProductoBEL.length && this.mandato === false) {
                this.getCumplimientoNoCliente(this.rutCliente, this.nombreCliente, this.apellidoCliente);
            } else {
                this.getCumplimientoCliente(this.rutCliente, this.nombreCliente, this.apellidoCliente);
            }
        } else {
            this.getCumplimientoNoCliente(this.rutCliente, this.nombreCliente, this.apellidoCliente);
        }
        this.accionFormulario(true);

        this.logCompliance.logData(
            this.cumplimientoForm.value,
            this.logtemp,
            "log_negocio",
        );
        this.gtmService.pushTagEventoInteractivo('evento-interactivo', 'Buscar estado Cumplimiento', 'Click', 'Buscar');
    }

    async getCumplimientoCliente(rutCliente: string, nombreCliente: string, apellidoCliente: string) {
        try {
            const mensajeObj: any = {};
            const [rutClienteLimpio] = this.utilService.limpiarRutCliente(rutCliente);
            const accionInversion = await this.clientRequestService.getAccionInversion(rutClienteLimpio, nombreCliente, apellidoCliente);
            const compliance = accionInversion?.compliance;

            let marcasActivas = [];
            const { MENSAJES_COMPLIANCE } = await this.featureVarsService.getMapped({ codigoVariable: 'MENSAJES_COMPLIANCE' });
            const { MENSAJES_COMPLIANCE_V2 } = await this.featureVarsService.getMapped({ codigoVariable: 'MENSAJES_COMPLIANCE_V2' });
            const marcasCompliance = MENSAJES_COMPLIANCE?.subFeatureVars;
            if (MENSAJES_COMPLIANCE?.estado === 'ACTIVO') {
                marcasActivas = marcasCompliance.filter(m => m.estado === 'ACTIVO');
            }
            const estadoGeneral: any = compliance.brands?.estadoGeneral ?? "Liberado";
            const marcasRaw = compliance.brands?.marcas?.marca;

            let items = '';

            if(marcasRaw){
              const marcas: any[] = Array.isArray(marcasRaw) ? marcasRaw : [marcasRaw];
              items = marcas.map((marca: any) => {
                  const codigoMarca = `CODIGO_${marca.codigo}_${marca.estado.codigo}`;
                  const mensaje = marcasActivas.find(marca => marca.codigo === codigoMarca)?.valor ?? null;
                  return mensaje ? `<li>${mensaje}</li>` : '';
              }).join('');
            }

            mensajeObj.color = estadoGeneral === 'Liberado' ? 'info' : 'warning';
            mensajeObj.titulo = 'Importante';
            mensajeObj.mensaje = `El RUT <b>${rutCliente}</b> ya es cliente.<br><ul style="list-style: disc !important;">${items}</ul>`;

            const codigoMarcaCompliance = items !== '' ? 2 : 1;
            await this.obtenerMensajeCompliance(rutCliente, codigoMarcaCompliance, mensajeObj);
        } catch (error) {
            this.gtmService.pushTagEventoInteractivo('evento-interactivo', 'Buscar estado Cumplimiento Cliente', 'Click', 'Sin Resultados');
            this.logtemp.status = Status.NOK
            this.logCompliance.logData(
                this.cumplimientoForm.value,
                this.logtemp,
                "log_error",
            );
            await this.obtenerMensajeCompliance();
            this.botonVisible = true;
        } finally {
          this.loaderService.hideLoading();
        }
    }

    async getCumplimientoNoCliente(rutCliente: string, nombreCliente: string, apellidoCliente: string) {
        try {
            const mensajeObj: any = {};
            nombreCliente = nombreCliente.toLocaleUpperCase();
            apellidoCliente = apellidoCliente.toLocaleUpperCase();
            const accionInversionNoCliente = await this.clientRequestService.getAccionInversionNoCliente(this.rutClienteLimpio, nombreCliente, apellidoCliente);
            const marcaCompliance = accionInversionNoCliente?.marcaCompliance;
            const estadoGeneral = accionInversionNoCliente?.brands?.estadoGeneral?._text ?? '';
            const codigoMarcaCompliance = marcaCompliance ? 3 : 4;
            mensajeObj.color = estadoGeneral === 'Liberado' ? 'info' : 'warning';
            mensajeObj.titulo = 'Importante';
            await this.obtenerMensajeCompliance(rutCliente, codigoMarcaCompliance, mensajeObj);
        } catch (error) {
            this.gtmService.pushTagEventoInteractivo('evento-interactivo', 'Buscar estado Cumplimiento No Cliente', 'Click', 'Sin Resultados');
            this.logtemp.status = Status.NOK
            this.logCompliance.logData(
                this.cumplimientoForm.value,
                this.logtemp,
                "log_error",
            );
            await this.obtenerMensajeCompliance();
            this.botonVisible = true;
        }
    }

    resetFormulario() {
        this.showMessage = false;
        this.mensajeCompliance = '';
        this.cumplimientoForm.reset();
        this.accionFormulario(false);
    }

    async obtenerMensajeCompliance(rut?: string, codigoMensaje?: number, mensajeObj:any = undefined) {
        let mensajes: any = mensajeObj;
        if(mensajeObj === undefined || !mensajeObj.hasOwnProperty('mensaje')) {
            let [datosMensaje] = mensajesCompliance(rut).filter(mensaje => mensaje.id === codigoMensaje);
            const mensajesNvos = await this.generaCompilance_v2(rut ?? '') ?? [];
            if (mensajesNvos.length > 0) {
                const codigo = `CODIGO_0${datosMensaje.id}`;

                const mensajeEncontrado = mensajesNvos.find(mensaje => mensaje.codigo === codigo);

                if (mensajeEncontrado) {
                    datosMensaje.mensaje = mensajeEncontrado.nota;
                    datosMensaje.titulo = mensajeEncontrado.valor;
                }
            }
            const mensajesTemp = datosMensaje ?? mensajePorDefecto();
            if(!mensajeObj.hasOwnProperty('mensaje')) {
                mensajes.mensaje = mensajesTemp.mensaje;
                if(datosMensaje){
                   mensajes.color = datosMensaje.color;
                   mensajes.titulo = datosMensaje.titulo;
                }
            } else {
                mensajes = mensajesTemp;
            }
        }
        this.mensajeCompliance = mensajes.mensaje;
        this.tipoMensaje = mensajes.color;
        this.tituloMensaje = mensajes.titulo;
        this.accionFormulario(true);
        this.loaderService.hideLoading();
    }

    accionFormulario(activarFormulario: boolean) {
        if (!activarFormulario) {
            this.cumplimientoForm.enable();
            this.botonVisible = false;
            this.activarBoton = false;
            this.showMessage = false;
        } else {
            this.cumplimientoForm.disable();
            this.botonVisible = true;
            this.activarBoton = true;
            this.showMessage = true;
        }
    }

    onCloseButton() {
        this.resetFormulario();
    }

    async generaCompilance_v2(campo: string){
        let mensajes = [];
        const { MENSAJES_COMPLIANCE_V2 } = await this.featureVarsService.getMapped({ codigoVariable: 'MENSAJES_COMPLIANCE_V2' });
        const mensajesCompliance = MENSAJES_COMPLIANCE_V2?.subFeatureVars;

        if (MENSAJES_COMPLIANCE_V2?.estado === 'ACTIVO') mensajes = mensajesCompliance.filter(m => m.estado === 'ACTIVO');

        const registro: Record<string, string> = { RUT: campo };

        mensajes = (mensajes ?? []).map(mensaje => ({ ...mensaje, nota: this.procesarMensaje(mensaje.nota ?? '', registro) }));

        return mensajes;
    }

    procesarMensaje(texto: string, campos: Record<string, string>): string {
        texto = texto.replace(
            /\{\{campo:([^}]+)\}\}/g,
            (_, atributo: string) => {
                return `<b>${campos[atributo] ?? ""}</b>`
            }
        );
        texto = texto.replace(
            /\{\{liga:([^}]+)\}\}\s*\{\{href:([^}]+)\}\}/g,
            (_, liga: string, href: string) => {
                return `<a href="${href}" target="_blank"><strong>${liga}</strong></a>`;
            }
        );
        return '<div style="display: block !important;">' + texto + '</div>';
    }
}
